import { supabase } from '../supabase';
import { logEvent, logFunnelSignupConverted } from '../analytics/eventLogger';
import { SOURCE_BRANCH } from '../../utils/deployInfo';

/**
 * Client-side wrapper for the convert_pending_profile RPC.
 *
 * Called from hydrateFromAuth() when no existing profile is found for the phone.
 * The RPC runs as a single transaction: re-key + profile creation + cleanup.
 * No parameters — derives everything from the auth session.
 *
 * @returns {{ error: string|null, profileUserId: string|null, anonName: string|null, connectionsMigrated: number, messagesMigrated: number, anonIdsMigrated: number, chatRoomId: string|null, convertedInPlace: boolean, accountStatus: string|null }}
 */
const failedResult = (message) => ({
  error: message,
  profileUserId: null,
  anonName: null,
  connectionsMigrated: 0,
  messagesMigrated: 0,
  anonIdsMigrated: 0,
  chatRoomId: null,
  convertedInPlace: false,
  accountStatus: null,
});

export const convertPendingProfile = async () => {
  // Pre-auth: no profileUsername or phone available at this point
  logEvent('pending_conversion_started');

  try {
    const { data, error } = await supabase.rpc('convert_pending_profile', {
      p_source_branch: SOURCE_BRANCH,
    });

    if (error) {
      console.error('[PENDING_CONVERSION] RPC failed:', error.message);
      logEvent('pending_conversion_failed', {
        step: 'rpc_call',
        error: error.message,
      });
      return failedResult(error.message);
    }

    const result = {
      error: null,
      profileUserId: data.profile_user_id,
      anonName: data.anon_name || null,
      connectionsMigrated: data.connections_migrated || 0,
      messagesMigrated: data.messages_migrated || 0,
      anonIdsMigrated: data.anon_ids_migrated || 0,
      chatRoomId: data.chat_room_id || null,
      // 038: a Simple profile upgraded in place keeps its id, name and data
      convertedInPlace: data.converted_in_place === true,
      accountStatus: data.account_status || null,
    };

    logEvent('pending_conversion_completed', {
      profile_user_id: result.profileUserId,
      connections_migrated: result.connectionsMigrated,
      messages_migrated: result.messagesMigrated,
      views_migrated: data.views_migrated || 0,
      anon_ids_migrated: result.anonIdsMigrated,
      converted_in_place: result.convertedInPlace,
      // Full id set claimed by v3 — forensics for identity-split debugging
      anon_ids: data.anon_ids || [],
    });

    // Fire funnel_signup_converted with chatRoomId from the pending record
    logFunnelSignupConverted(
      result.profileUserId,
      result.anonName,
      result.connectionsMigrated,
      null, // connectedWith populated after data recovery
      result.chatRoomId,
      result.anonIdsMigrated
    );

    return result;
  } catch (err) {
    console.error('[PENDING_CONVERSION] Unexpected error:', err);
    logEvent('pending_conversion_failed', {
      step: 'unexpected',
      error: err.message,
    });
    return failedResult(err.message);
  }
};
